"use client";

import { useRef } from "react";
import { previewSrc, type Attachment } from "@/lib/postRaw";
import { FileChip } from "./FileChip";

/**
 * What a composer or editor has attached: images and video as reorderable
 * thumbnails, other files as chips.
 *
 * Reordering uses arrows rather than drag-and-drop — the same control on a
 * phone, a trackpad and a keyboard — and only media is reorderable, since
 * order is what decides the gallery layout. Files render as a list either way.
 */
/**
 * Make the tile show a frame, not an empty square (OSO-486).
 *
 * `preload="metadata"` gets the header — duration, dimensions — and on desktop
 * Chrome and Firefox the element paints the first frame off the back of it.
 * iOS Safari does not: it holds the metadata and paints nothing until a frame
 * has actually been decoded, so the tray tile stayed the bare `bg-muted` grey
 * with a play badge floating on it. That is what was reported — a video with
 * no thumbnail the moment it was attached, on the composer's own preview,
 * before any post exists and so before the transcode job's poster
 * (media-pipeline §3.6) has anything to do with it.
 *
 * Nudging `currentTime` off zero forces the decode, and the frame it lands on
 * is the thumbnail. A clip shorter than the seek target would clamp to its own
 * end — often a fade to black — so anything under 0.2 s seeks to its midpoint
 * instead.
 */
function paintFirstFrame(event: React.SyntheticEvent<HTMLVideoElement>) {
  const video = event.currentTarget;
  if (video.currentTime > 0) return;
  const duration = video.duration;
  video.currentTime = Number.isFinite(duration) && duration < 0.2 ? duration / 2 : 0.1;
}

/**
 * A fraction as whole per cent, FLOORED (OSO-705). The bytes are not the whole
 * wait — a Bunny upload hashes the file first and the ticket and the last
 * chunk's acknowledgement come after — so rounding 0.999 up to 100 leaves a
 * bar that says "done" while the tile is still working and the button still
 * disabled. Flooring says 99 until it is genuinely finished, at which point
 * the tile stops being an upload at all.
 */
function percent(fraction: number): number {
  return Math.max(0, Math.min(99, Math.floor(fraction * 100)));
}

export function AttachmentTray({
  attachments,
  onRemove,
  onMove,
  size = "md",
}: {
  attachments: Attachment[];
  onRemove: (id: string) => void;
  /** Indices are into the full attachment list, not the media subset. */
  onMove: (from: number, to: number) => void;
  /** "sm" for the tighter comment rows. */
  size?: "sm" | "md";
}) {
  const media = attachments
    .map((a, index) => ({ a, index }))
    .filter(({ a }) => a.kind !== "file");
  const files = attachments.filter((a) => a.kind === "file");

  const small = size === "sm";
  const btn = small ? "w-6 h-6" : "w-7 h-7";
  const icon = small ? 11 : 13;

  return (
    <div className={small ? "mt-2 space-y-2" : "mt-3 space-y-2"}>
      {media.length > 0 && (
        <ul
          className={`grid gap-2 ${small ? "grid-cols-4 sm:grid-cols-6" : "grid-cols-3 sm:grid-cols-4"}`}
        >
          {media.map(({ a, index }, position) => (
            <li
              key={a.id}
              className="relative aspect-square rounded-lg overflow-hidden bg-muted"
            >
              {a.kind === "video" ? (
                <>
                  {/* A silent, never-playing still: this tile is a thumbnail,
                      so it loads the header for its first frame and nothing
                      more, and it stays inline on iOS (OSO-437). */}
                  <video
                    src={previewSrc(a)}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                    onLoadedMetadata={paintFirstFrame}
                  />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </>
              ) : (
                <img src={previewSrc(a)} alt="" className="w-full h-full object-cover" />
              )}

              {/* WHICH WAIT THIS IS (OSO-490, then OSO-578). There are two of
                  them now and they ask opposite things of the member:

                    UPLOADER…  bytes are going up. You MUST wait — the
                               attachment has no id yet, so it cannot reach the
                               request, and "Opret opslag" is disabled.
                    BEHANDLES  the server is transcoding. You must NOT wait —
                               the button is live and the row publishes itself
                               when the video lands.

                  Only the second one had words. The first was a bare spinner
                  over the tile, so the disabled button looked like it was
                  waiting for the transcode — the one thing it is not waiting
                  for. Both now say which wait they are, in the same corner, so
                  the difference is readable rather than inferable.

                  The row stays removable and reorderable while it encodes; it
                  is not removable while it uploads, because there is nothing
                  on the server to remove yet.

                  TOP-LEFT, NOT BOTTOM-LEFT: the reorder row below is
                  `bottom-1 left-1 right-1` and renders after this, so a
                  bottom-left badge is painted over by the "flyt til venstre"
                  arrow the moment there is more than one attachment — which
                  is exactly when a member most needs to know which tile is
                  the one still encoding. Top-right is the remove button; top
                  -left is the corner nothing else claims.

                  `failed` IS BADGED TOO, and it is the tile with no preview to
                  explain itself: `url` is null, so the video element has
                  nothing to paint and the square is otherwise blank. An editor
                  opened on a post whose transcode failed is the one place this
                  is reached (`fromDocument` carries the status through). */}
              {(a.uploading || a.status !== "ready") && (
                <span
                  // `z-10`: the uploading overlay below is a sibling painted
                  // after this one, so without it the badge is under the dim.
                  className={`absolute top-1 left-1 z-10 px-1.5 py-0.5 rounded-full text-white text-[10px] font-medium leading-none ${
                    !a.uploading && a.status === "failed"
                      ? "bg-destructive"
                      : "bg-black/70"
                  }`}
                >
                  {a.uploading
                    ? a.progress == null
                      ? "Uploader…"
                      : `Uploader… ${percent(a.progress)} %`
                    : a.status === "failed"
                      ? "Mislykkedes"
                      : "Behandles"}
                </span>
              )}

              {a.uploading ? (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center px-3">
                  {a.progress == null ? (
                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <div
                      role="progressbar"
                      aria-valuenow={percent(a.progress)}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuetext={`Uploader… ${percent(a.progress)} %`}
                      className="w-full h-1.5 rounded-full bg-white/30 overflow-hidden"
                    >
                      <div
                        className="h-full bg-white transition-[width] duration-200 ease-out"
                        style={{ width: `${percent(a.progress)}%` }}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => onRemove(a.id)}
                    aria-label={`Fjern ${a.kind === "video" ? "video" : "billede"} ${position + 1}`}
                    title="Fjern"
                    className={`absolute top-1 right-1 ${btn} rounded-full bg-black/60 hover:bg-black/80 text-white flex items-center justify-center transition-colors`}
                  >
                    <svg width={icon} height={icon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>

                  {media.length > 1 && (
                    <div className="absolute bottom-1 left-1 right-1 flex items-center justify-between">
                      <button
                        type="button"
                        onClick={() => onMove(index, media[position - 1]?.index ?? index)}
                        disabled={position === 0}
                        aria-label={`Flyt ${position + 1} til venstre`}
                        title="Flyt til venstre"
                        className={`${btn} rounded-full bg-black/60 text-white flex items-center justify-center transition-colors enabled:hover:bg-black/80 disabled:opacity-0`}
                      >
                        <svg width={icon + 1} height={icon + 1} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="15 18 9 12 15 6" />
                        </svg>
                      </button>
                      <span className="px-1.5 rounded-full bg-black/60 text-white text-[11px] font-medium leading-5">
                        {position + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => onMove(index, media[position + 1]?.index ?? index)}
                        disabled={position === media.length - 1}
                        aria-label={`Flyt ${position + 1} til højre`}
                        title="Flyt til højre"
                        className={`${btn} rounded-full bg-black/60 text-white flex items-center justify-center transition-colors enabled:hover:bg-black/80 disabled:opacity-0`}
                      >
                        <svg width={icon + 1} height={icon + 1} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {files.map((a) => (
        <FileChip
          key={a.id}
          name={a.name}
          action={
            a.uploading ? (
              // Words here too (OSO-578): a chip has room for them, and a bare
              // spinner beside a filename is the same unanswered "waiting for
              // what?" the video tile had.
              <span className="flex items-center gap-1.5 shrink-0 text-[11px] text-subtle-foreground">
                Uploader…
                <span className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
              </span>
            ) : (
              <button
                type="button"
                onClick={() => onRemove(a.id)}
                aria-label={`Fjern ${a.name}`}
                title="Fjern"
                className="w-6 h-6 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-subtle-foreground transition-colors shrink-0"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            )
          }
        />
      ))}
    </div>
  );
}

/** The button that opens a file picker, plus its hidden input. */
export function AddAttachmentButton({
  onFiles,
  accept = "image/*,video/*",
  label = "Tilføj billede",
  className,
  children,
}: {
  onFiles: (files: FileList | null) => void;
  accept?: string;
  label?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        title={label}
        aria-label={label}
        className={
          className ??
          "w-9 h-9 rounded-full hover:bg-green-50 dark:hover:bg-gray-800 flex items-center justify-center text-green-500 transition-colors"
        }
      >
        {children ?? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        )}
      </button>
      <input
        type="file"
        ref={inputRef}
        className="hidden"
        accept={accept}
        multiple
        onChange={(e) => {
          onFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </>
  );
}
